import { useCallback, useState } from 'react';
import { useApiErrorTranslation } from './useApiErrorTranslation';
import { useApiRequest } from './useApiRequest';
import { AIRPORTS, HIDE_NARITA_AIRPORT } from '@/constants/area';

export interface AirportFlightType {
  airport: string;
  flightType: string;
}

export interface LoungeOption {
  lounge: string;
  areaIds: number[];
  accessible: boolean;
}

/**
 * エリア情報の段階的取得を行うカスタムフック
 * スペース種別選択→空港選択→ターミナル選択→ラウンジ選択の流れで段階的にデータを取得
 */
export const useAreas = () => {
  const [airports, setAirports] = useState<AirportFlightType[]>([]);
  const [terminals, setTerminals] = useState<string[]>([]);
  const [lounges, setLounges] = useState<LoungeOption[]>([]);
  // スタッフ用: 全ターミナル・全ラウンジ（フィルター条件なし）
  const [allTerminals, setAllTerminals] = useState<string[]>([]);
  const [allLounges, setAllLounges] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const { translateApiError } = useApiErrorTranslation();

  const { isRequesting: isRequestingAirports, execute: executeAirports } =
    useApiRequest('GET', 'AreasAirports');

  const { isRequesting: isRequestingTerminals, execute: executeTerminals } =
    useApiRequest('GET', 'AreasTerminals');

  const { isRequesting: isRequestingLounges, execute: executeLounges } =
    useApiRequest('GET', 'AreasLounges');

  // スタッフ用（全ターミナル・全ラウンジ取得）には既存のAPIをパラメータなしで使用
  const {
    isRequesting: isRequestingAllTerminals,
    execute: executeAllTerminals,
  } = useApiRequest('GET', 'AreasTerminals');

  const { isRequesting: isRequestingAllLounges, execute: executeAllLounges } =
    useApiRequest('GET', 'LoungeNames');

  /**
   * 空港+便種別リストを取得
   * @param spaceType スペース種別
   */
  const fetchAirports = useCallback(
    async (spaceType: string) => {
      if (!spaceType) {
        setAirports([]);
        setTerminals([]);
        setLounges([]);
        return;
      }

      try {
        const result = await executeAirports({ space_type: spaceType });

        if (!result.ok) {
          const { message } = translateApiError(result.error);
          setError(message);
          setAirports([]);
          return;
        }

        const airportList = result.data.data
          .map((item) => ({
            airport: item.airport,
            flightType: item.flight_type,
          }))
          .filter(
            (item) =>
              !HIDE_NARITA_AIRPORT || item.airport !== AIRPORTS.NARITA,
          );

        setAirports(airportList);
        setError(null);
      } catch (err) {
        const { message } = translateApiError(
          err instanceof Error ? err.message : undefined,
        );
        setError(message);
        setAirports([]);
      }
    },
    [executeAirports, translateApiError],
  );

  /**
   * ターミナルリストを取得
   */
  const fetchTerminals = useCallback(
    async (spaceType: string, airport: string, flightType: string) => {
      if (!spaceType || !airport || !flightType) {
        setTerminals([]);
        setLounges([]);
        return;
      }

      // 新しいターミナルリストを取得する前に既存のリストをクリア
      setTerminals([]);
      setLounges([]);

      try {
        const result = await executeTerminals({
          space_type: spaceType,
          airport: airport,
          flight_type: flightType,
        });

        if (!result.ok) {
          const { message } = translateApiError(result.error);
          setError(message);
          setTerminals([]);
          return;
        }

        setTerminals(result.data.data);
        setLounges([]);
        setError(null);
      } catch (err) {
        const { message } = translateApiError(
          err instanceof Error ? err.message : undefined,
        );
        setError(message);
        setTerminals([]);
      }
    },
    [executeTerminals, translateApiError],
  );

  /**
   * ラウンジリストを取得
   */
  const fetchLounges = useCallback(
    async (
      spaceType: string,
      airport: string,
      flightType: string,
      terminal: string,
    ) => {
      if (!spaceType || !airport || !flightType || !terminal) {
        setLounges([]);
        return;
      }

      // 新しいラウンジリストを取得する前に既存のリストをクリア
      setLounges([]);

      try {
        const result = await executeLounges({
          space_type: spaceType,
          airport: airport,
          flight_type: flightType,
          terminal: terminal,
        });

        if (!result.ok) {
          const { message } = translateApiError(result.error);
          setError(message);
          setLounges([]);
          return;
        }

        const loungeList = result.data.data.map((item) => ({
          lounge: item.lounge,
          areaIds: item.area_ids,
          accessible: item.accessible,
        }));
        setLounges(loungeList);
        setError(null);
      } catch (err) {
        const { message } = translateApiError(
          err instanceof Error ? err.message : undefined,
        );
        setError(message);
        setLounges([]);
      }
    },
    [executeLounges, translateApiError],
  );

  /**
   * ターミナルリストを取得（スタッフ用・オプションで空港フィルター可）
   */
  const fetchAllTerminals = useCallback(
    async (spaceType: string, airport?: string, flightType?: string) => {
      if (!spaceType) {
        setAllTerminals([]);
        return;
      }

      try {
        const result = await executeAllTerminals({
          space_type: spaceType,
          airport,
          flight_type: flightType,
        });

        if (!result.ok) {
          const { message } = translateApiError(result.error);
          setError(message);
          setAllTerminals([]);
          return;
        }

        setAllTerminals(result.data.data);
        setError(null);
      } catch (err) {
        const { message } = translateApiError(
          err instanceof Error ? err.message : undefined,
        );
        setError(message);
        setAllTerminals([]);
      }
    },
    [executeAllTerminals, translateApiError],
  );

  /**
   * ラウンジリストを取得（スタッフ用・オプションで空港/ターミナルフィルター可）
   */
  const fetchAllLounges = useCallback(
    async (
      spaceType: string,
      airport?: string,
      flightType?: string,
      terminal?: string,
    ) => {
      if (!spaceType) {
        setAllLounges([]);
        return;
      }

      try {
        const result = await executeAllLounges({
          space_type: spaceType,
          airport,
          flight_type: flightType,
          terminal,
        });

        if (!result.ok) {
          const { message } = translateApiError(result.error);
          setError(message);
          setAllLounges([]);
          return;
        }

        setAllLounges(result.data.data);
        setError(null);
      } catch (err) {
        const { message } = translateApiError(
          err instanceof Error ? err.message : undefined,
        );
        setError(message);
        setAllLounges([]);
      }
    },
    [executeAllLounges, translateApiError],
  );

  return {
    // データ
    airports,
    terminals,
    lounges,
    allTerminals,
    allLounges,

    // ローディング状態
    loading:
      isRequestingAirports || isRequestingTerminals || isRequestingLounges,
    isLoadingAirports: isRequestingAirports,
    isLoadingTerminals: isRequestingTerminals,
    isLoadingLounges: isRequestingLounges,
    isLoadingAllTerminals: isRequestingAllTerminals,
    isLoadingAllLounges: isRequestingAllLounges,

    // エラー
    error,

    // データ取得関数
    fetchAirports,
    fetchTerminals,
    fetchLounges,
    fetchAllTerminals,
    fetchAllLounges,
  };
};
